import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { createContext, useCallback, useContext, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { useAgentStore } from "../agent/agentStore";
import { useDraftStore } from "../agent/draftStore";
import { RefusalError, setCsrfToken } from "../api/client";
import { queryKey, runCommand } from "../api/endpoints";
import { useApiCommand, useMe } from "../api/hooks";
import type { MeView } from "../api/types";
import { dropWorkspaceQueries } from "../live/sink";
import { useLiveStore } from "../live/store";
import { useToastStore } from "../ui/toastStore";

const MeContext = createContext<MeView | null>(null);

/** Makes the signed-in person known to everything below it. The routes below `AuthGate` are inside it. */
export function MeProvider({ me, children }: { me: MeView; children: ReactNode }) {
  return <MeContext.Provider value={me}>{children}</MeContext.Provider>;
}

/** The signed-in person. Used by every screen except sign-in. */
export function useCurrentMe(): MeView {
  const me = useContext(MeContext);
  if (!me) throw new Error("useCurrentMe was used outside the signed-in part of the app");
  return me;
}

/** Take a `MeView` (from sign-in, a switch of discipline, a password change) as the current person. */
export function adoptMe(queryClient: QueryClient, me: MeView): void {
  setCsrfToken(me.csrf_token);
  queryClient.setQueryData(queryKey("me", {}) as unknown as QueryKey, me);
}

/** Forget everything that belongs to the person who was signed in. */
export function resetSessionState(queryClient: QueryClient): void {
  setCsrfToken(null);
  queryClient.clear();
  useLiveStore.getState().reset();
  useAgentStore.getState().reset();
  useDraftStore.getState().clear();
  useToastStore.getState().clear();
}

export type Session =
  | { status: "loading" }
  | { status: "signed_out" }
  | { status: "error"; error: unknown; retry: () => void }
  | { status: "ready"; me: MeView };

/** Whether the page is signed in, from the `me` view. */
export function useSession(): Session {
  const query = useMe();
  if (query.error instanceof RefusalError && query.error.code === "not_signed_in") return { status: "signed_out" };
  if (query.data) return { status: "ready", me: query.data };
  if (query.isPending) return { status: "loading" };
  return { status: "error", error: query.error, retry: () => void query.refetch() };
}

/** End the session on the server, forget what the page holds, and go to the sign-in screen. */
export function useSignOut(): () => Promise<void> {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useCallback(async () => {
    try {
      await runCommand("sign_out", {});
    } catch {
      // The session may be over already; the page is cleared either way.
    }
    resetSessionState(queryClient);
    void navigate("/sign-in", { replace: true });
  }, [queryClient, navigate]);
}

/** Work in another discipline (or in the practice space). The page forgets what it read in the old one. */
export function useSwitchSpace() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { mutateAsync, isPending, error } = useApiCommand("set_active_workspace");
  const switchTo = useCallback(
    async (workspaceCode: string, space: "workspace" | "practice" = "workspace") => {
      const reply = await mutateAsync({ workspace_code: workspaceCode, space });
      adoptMe(queryClient, reply.data);
      dropWorkspaceQueries(queryClient);
      void navigate("/");
    },
    [mutateAsync, queryClient, navigate],
  );
  return { switchTo, isPending, error };
}
